<script setup lang="ts">
import { Icon } from '@iconify/vue'
import { computed, isProxy } from 'vue'
import { hex2rgba, getCssVarValue } from '@/util'
import { MinerStatus } from '@/types/rc/miners/MinerStatus'
import { ClaimStatus, ClaimStatusTypes } from '@/types/asicw/claims'

interface Props {
  status: string
  type: ClaimStatusTypes
  rejectReason?: string
  readonly?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void
}>()

// Check if the status is a Proxy
const isLoading = computed(() => {
  return isProxy(props.status)
})

const claimStatusColorMap: Record<string, string> = {
  [ClaimStatus.UnderReview]: 'var(--status-under-review)',
  [ClaimStatus.ConditionalApproval]: 'var(--status-conditional-approval)',
  [ClaimStatus.PendingCustomerAuthorization]: 'var(--status-pending-customer-auth)',
  [ClaimStatus.Authorized]: 'var(--status-authorized)',
  [ClaimStatus.Approved]: 'var(--status-approved)',
  [ClaimStatus.Denied]: 'var(--status-denied)',
  [ClaimStatus.Completed]: 'var(--status-completed)',
  [ClaimStatus.Invoiced]: 'var(--status-completed)',
}

// TODO: Define custom color set for each miner status
const repairStatusColorMap: Record<string, string> = {
  [MinerStatus.Inventoried]: 'var(--status-under-review)',
  [MinerStatus.Cleaned]: 'var(--status-under-review)',
  [MinerStatus.Diagnosed]: 'var(--status-under-review)',
  [MinerStatus.Blocked]: 'var(--status-denied)',
  [MinerStatus.RepairAuthRequested]: 'var(--status-conditional-approval)',
  [MinerStatus.RepairAuthDenied]: 'var(--status-denied)',
  [MinerStatus.RepairInProgress]: 'var(--status-conditional-approval)',
  [MinerStatus.FailedStressTest]: 'var(--status-conditional-approval)',
  [MinerStatus.Completed]: 'var(--status-completed)',
}

// Status colors mapping
const getStatusColor = (status: string): string => {
  if (props.type == ClaimStatusTypes.ClaimStatus) {
    return claimStatusColorMap[status] || 'var(--status-default)'
  } else {
    return repairStatusColorMap[status] || 'var(--status-default)'
  }
}

const getStatusLabel = (value: string) => {
  if (!value) return 'Set Status'
  return value
}
</script>

<template>
  <el-skeleton v-if="isLoading" :rows="1" animated>
    <template #template>
      <el-skeleton-item variant="text" style="width: 120px; height: 24px" animated />
    </template>
  </el-skeleton>
  <el-tag
    v-else
    disable-transitions
    :color="`${hex2rgba(getCssVarValue(getStatusColor(status)), 0.2)}`"
    class="status-tag"
    style="border: none"
    @click="(event: any) => !readonly && emit('click', event)"
  >
    <span :style="{ color: getStatusColor(status) }">{{ getStatusLabel(status) }}</span>
    <Icon
      v-if="!readonly"
      :color="getStatusColor(status)"
      icon="material-symbols:keyboard-arrow-down-rounded"
      :height="16"
    />
  </el-tag>
</template>

<style scoped lang="scss">
.status-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;

  :deep(.el-tag__content) {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
}

:deep(.el-skeleton) {
  display: flex;
  align-items: center;
}

:deep(.el-skeleton__item) {
  margin: 0;
}
</style>
